import type { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { GovErrorCode } from '@gov-design-system-ce/react';
import type { Designation } from '@gov-nx/api';
import { MarkdownRender } from '@gov-nx/component/web';
import { useExtraConfigContext } from '@gov-nx/core/app';
import {
	LocalizeNameSpaceTypes,
	useCmsService,
	useLocale,
} from '@gov-nx/core/service';
import type { ServiceCode } from '@gov-nx/module/service';
import { useAuthStore } from '@gov-nx/store';
import { Button, ContentLayout } from '@gov-nx/ui/web';
import { useSessionStoredUrl } from '@gov-nx/utils/web';
import { pageRouteResolver } from '@gov-nx/web';

interface RezaTypeProtectedContentProps extends PropsWithChildren {
	code: ServiceCode;
}

export const RezaTypeProtectedContent = ({
	children,
	code,
}: RezaTypeProtectedContentProps) => {
	const cmsContent = useCmsService({ code });
	const setActiveReza = useAuthStore('setActiveReza');
	const activeRezaDesignation = useAuthStore('activeRezaDesignation');
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const { rememberCurrentPathName } = useSessionStoredUrl();
	const navigate = useNavigate();
	const { t } = useLocale(LocalizeNameSpaceTypes.Service);

	const onRezaChangeRequest = () => {
		setActiveReza(null);
		rememberCurrentPathName();
		navigate(pageRouteResolver('reza-vyber'));
	};

	if (cmsContent == null) {
		return null;
	}

	if (application !== 'pp' || cmsContent.designation === undefined) {
		return children;
	}

	// `zastupujiciOsobaTyp` only distinguishes 'FO'/'PO', so PFO users would never
	// pass services designated for 'pfo'. `activeRezaDesignation` ('fo'|'pfo'|'po')
	// comes from okz-changed and correctly distinguishes PFO.
	const designation = activeRezaDesignation ?? 'fo';

	const supportedDesignations = (cmsContent.designation ?? []).map(
		(d) => d.toLocaleLowerCase() as Designation
	);

	if (
		!supportedDesignations.includes(
			designation.toLocaleLowerCase() as Designation
		)
	) {
		// Tell the user which representation type(s) the service supports so they
		// know what to switch to. Mirrors the fixed-key-per-combination approach
		// used by `DataBoxRequiredBanner`. Falls back to the original generic
		// sentence when no designation is set (e.g. empty list from the CMS).
		const isFO = supportedDesignations.includes('fo');
		const isPFO = supportedDesignations.includes('pfo');
		const isPO = supportedDesignations.includes('po');

		const descriptionKey = (() => {
			if (isFO && isPFO && isPO) return 'opravneni.popis.potreba-fo-pfo-po';
			if (isPFO && isPO) return 'opravneni.popis.potreba-pfo-po';
			if (isFO && isPO) return 'opravneni.popis.potreba-fo-po';
			if (isFO && isPFO) return 'opravneni.popis.potreba-fo-pfo';
			if (isPO) return 'opravneni.popis.potreba-po';
			if (isPFO) return 'opravneni.popis.potreba-pfo';
			if (isFO) return 'opravneni.popis.potreba-fo';
			return 'opravneni.popis.pro-zobrazeni-se-musite-prihlasit-v-zastoupeni';
		})();

		return (
			<ContentLayout>
				<GovErrorCode>
					<p
						className="text-headline-l!"
						slot="headline">
						{t('opravneni.nadpis.nemate-dostatecne-opravneni')}
					</p>
					<MarkdownRender>{t(descriptionKey)}</MarkdownRender>
					<Button
						onClick={onRezaChangeRequest}
						type="solid"
						color="primary"
						size="m">
						{t('opravneni.akce.zmenit-opravneni')}
					</Button>
				</GovErrorCode>
			</ContentLayout>
		);
	}

	return children;
};
